import React, { useState, useMemo, useEffect } from "react";
import { 
  Share2, 
  Download,
  Video,
  Users,
  Star,
  FileText,
  ChevronRight,
  Play,
  Volume2,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { Skeleton } from "../../components/ui/skeleton";

// Import your components
import { VideoPlayer } from "../../components/critiques_user/VideoPlayer";
import { JudgeCard } from "../../components/critiques_user/JudgeCard";
import { ScoreCard } from "../../components/critiques_user/ScoreCard";
import { PerformanceHistory } from "../../components/critiques_user/PerformanceHistory";
import { JudgeFeedback } from "../../components/critiques_user/JudgeFeedback";
import { EventsList } from "../../components/critiques_user/EventsList";
import { ShareModal } from "../../components/critiques_user/ShareModal";
import { PerformanceHistoryModal } from "../../components/critiques_user/PerformanceHistoryModal";

// First declare MOCK_EVENTS
const MOCK_EVENTS = [
  {
    id: 'event-1',
    name: 'Spring Competition',
    date: '2024-04-12',
    routines: [
      {
        id: 'routine-1',
        title: 'Three Channel Critique',
        category: 'Dance Performance',
        hasVideo: true,
        videoUrl: '/test-videos/three_channel_critique.mov',
        award: 'High Gold',
        judges: [
          {
            name: 'Judge 1',
            photoUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alice',
            comment: 'Great energy and strong technique!',
            score: 95,
            scoreBreakdown: {
              Technique: 19,
              Choreography: 19,
              Musicality: 19,
              Performance: 19,
              Execution: 19
            }
          },
          {
            name: 'Judge 2',
            photoUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bob',
            comment: 'Excellent transitions and performance quality.',
            score: 92,
            scoreBreakdown: {
              Technique: 18,
              Choreography: 19,
              Musicality: 18,
              Performance: 19,
              Execution: 18
            }
          },
          {
            name: 'Judge 3',
            photoUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Carol',
            comment: 'Beautiful performance! Work on consistent energy throughout.',
            score: 90,
            scoreBreakdown: {
              Technique: 18,
              Choreography: 18,
              Musicality: 18,
              Performance: 18,
              Execution: 18
            }
          }
        ],
        performanceHistory: [
          {
            eventName: 'Winter Showcase',
            location: 'New York',
            score: 280,
            placement: 2,
            scoreBreakdown: {
              Technique: 19,
              Choreography: 19,
              Musicality: 19,
              Performance: 19,
              Execution: 19
            }
          }
        ]
      }
    ]
  }
];

// Then declare DEFAULT_ROUTINE
const DEFAULT_ROUTINE = MOCK_EVENTS[0].routines[0];

const DEFAULT_EVENT = {
  id: 'no-event',
  name: 'No Event',
  date: new Date().toISOString(),
  routines: [DEFAULT_ROUTINE]
};

const CritiquesPage = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Events data and selection
  const [events, setEvents] = useState(MOCK_EVENTS);
  const [selectedEvent, setSelectedEvent] = useState(MOCK_EVENTS[0]);

  // Routine selection
  const [selectedRoutine, setSelectedRoutine] = useState(DEFAULT_ROUTINE);

  // Judge selection for feedback
  const [selectedJudge, setSelectedJudge] = useState(0);
  // Add state for all-channel mode
  const [allChannelsMode, setAllChannelsMode] = useState(false);

  // History modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoricalEvent, setSelectedHistoricalEvent] = useState(null);

  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Mobile-specific states
  const [showMobileRoutineSelector, setShowMobileRoutineSelector] = useState(false);
  const [showJudgeDetails, setShowJudgeDetails] = useState(false);
  const [expandedSection, setExpandedSection] = useState('judges'); // 'judges', 'feedback', 'scores'
  const [isMobileControlsVisible, setIsMobileControlsVisible] = useState(true);

  // Example calculation of total score and award (replace with your actual logic)
  const totalScore = useMemo(() => {
    // If your logic is different, place it here; 
    // below just sums up all judges' scores in selectedRoutine
    if (!selectedRoutine || !selectedRoutine.judges) return 0;
    return selectedRoutine.judges.reduce((acc, j) => acc + (j.score || 0), 0);
  }, [selectedRoutine]);

  const award = selectedRoutine.award || "High Gold"; // or pull from your own logic

  // Handlers for event and routine selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    if (event.routines && event.routines.length > 0) {
      setSelectedRoutine(event.routines[0]);
      setSelectedJudge(0);
    } else {
      setSelectedRoutine(DEFAULT_ROUTINE);
    }
    // Close mobile routine selector if open
    setShowMobileRoutineSelector(false);
  };

  const handleRoutineSelect = (routine) => {
    setSelectedRoutine(routine);
    setSelectedJudge(0);
    // Close mobile routine selector if open
    setShowMobileRoutineSelector(false);
  };

  // Add loading state
  const [isLoading] = useState(false);

  // Move calculateAverageScore inside component to access events state
  const calculateAverageScore = () => {
    let totalScore = 0;
    let count = 0;
    
    events.forEach(event => {
      event.routines.forEach(routine => {
        routine.judges.forEach(judge => {
          if (judge.score) {
            totalScore += judge.score;
            count++;
          }
        });
      });
    });

    return count > 0 ? Math.round(totalScore / count) : 0;
  };

  // Add SkeletonStatCard component
  const SkeletonStatCard = () => (
    <div className={`rounded-xl shadow ${
      isDarkMode 
        ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
        : 'bg-base-100'
    }`}>
      <div className="p-5">
        <div className="flex items-center">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="ml-5 w-0 flex-1">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-6 w-12" />
          </div>
        </div>
      </div>
    </div>
  );

  // Add state to track main sidebar collapse state
  const [isMainSidebarCollapsed, setIsMainSidebarCollapsed] = useState(false);
  
  // Effect to hide mobile controls after 3 seconds of inactivity
  useEffect(() => {
    if (!isMobileControlsVisible) return;
    
    const timer = setTimeout(() => {
      setIsMobileControlsVisible(false);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [isMobileControlsVisible]);
  
  // Toggle mobile controls visibility
  const toggleMobileControls = () => {
    setIsMobileControlsVisible(prev => !prev);
  };

  // Effect to listen for main sidebar state changes
  useEffect(() => {
    const handleSidebarChange = () => {
      const sidebar = document.querySelector('aside');
      setIsMainSidebarCollapsed(sidebar?.classList.contains('w-16') ?? false);
    };

    handleSidebarChange();
    
    const observer = new MutationObserver(handleSidebarChange);
    const sidebar = document.querySelector('aside');
    
    if (sidebar) {
      observer.observe(sidebar, { attributes: true });
    }

    return () => observer.disconnect();
  }, []);

  // Check if we're on mobile viewport
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Set up event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Mobile UI Components
  const MobileRoutineSelector = () => (
    <div className={`fixed inset-0 z-50 bg-black/80 backdrop-blur-sm transition-all duration-300 ${
      showMobileRoutineSelector ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}>
      <div className="absolute top-0 right-0 bottom-0 w-4/5 max-w-xs bg-base-100 dark:bg-[#1a1f2d] shadow-xl flex flex-col">
        <div className="p-4 border-b border-base-300 dark:border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-medium dark:text-white">Select Critique</h3>
          <button 
            onClick={() => setShowMobileRoutineSelector(false)}
            className="p-2 rounded-full hover:bg-base-200 dark:hover:bg-white/10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 p-2">
          {events.map(event => (
            <div key={event.id} className="mb-4">
              <div className={`p-2 rounded-md mb-2 ${
                event.id === selectedEvent.id 
                  ? 'bg-primary/20 dark:bg-primary/20' 
                  : 'bg-base-200 dark:bg-white/5'
              }`}>
                <h4 className="font-medium dark:text-white">{event.name}</h4>
                <p className="text-xs text-base-content/70 dark:text-gray-400">
                  {new Date(event.date).toLocaleDateString()}
                </p>
              </div>
              
              <div className="pl-4 space-y-2">
                {event.routines.map(routine => (
                  <div 
                    key={routine.id}
                    onClick={() => handleRoutineSelect(routine)}
                    className={`p-2 rounded-md cursor-pointer flex items-center ${
                      selectedRoutine.id === routine.id 
                        ? 'bg-primary/10 dark:bg-primary/10' 
                        : 'hover:bg-base-200 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className="w-8 h-8 rounded bg-base-300 dark:bg-white/10 flex items-center justify-center mr-3">
                      <Play className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className={`font-medium ${
                        selectedRoutine.id === routine.id 
                          ? 'text-primary dark:text-primary' 
                          : 'dark:text-white'
                      }`}>
                        {routine.title}
                      </p>
                      <p className="text-xs text-base-content/70 dark:text-gray-400">
                        {routine.category}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Mobile bottom sheet component
  const MobileBottomSheet = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-base-100 dark:bg-[#1a1f2d] rounded-t-xl shadow-lg z-40 transition-transform duration-300 max-h-[80vh] overflow-hidden">
      <div className="flex justify-center p-2">
        <div className="w-12 h-1 bg-base-300 dark:bg-white/20 rounded-full"></div>
      </div>
      
      <div className="p-3 border-b border-base-300 dark:border-white/10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium dark:text-white">{selectedRoutine.title}</h3>
            <p className="text-xs text-base-content/70 dark:text-gray-400">
              {selectedRoutine.category}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-base-200 dark:bg-white/10">
              <Star className="h-3 w-3 text-yellow-500" />
              <span className="text-xs font-medium dark:text-white">{totalScore}</span>
            </div>
            <div className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
              {award}
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-2 border-b border-base-300 dark:border-white/10">
        <div className="flex justify-between">
          {['judges', 'feedback', 'scores'].map(section => (
            <button
              key={section}
              onClick={() => setExpandedSection(section)}
              className={`flex-1 p-2 text-center capitalize text-sm ${
                expandedSection === section
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-base-content/70 dark:text-gray-400'
              }`}
            >
              {section}
            </button>
          ))}
        </div>
      </div>
      
      <div className="overflow-y-auto p-3" style={{ maxHeight: '50vh' }}>
        {expandedSection === 'judges' && (
          <div className="space-y-4">
            {selectedRoutine.judges.map((judge, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg ${
                  selectedJudge === index 
                    ? 'bg-primary/20 dark:bg-primary/20' 
                    : 'bg-base-200 dark:bg-white/5'
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                    {judge.photoUrl ? (
                      <img src={judge.photoUrl} alt={judge.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full bg-gray-200">
                        <Users className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium dark:text-white">{judge.name}</h3>
                    <p className="text-xs text-base-content/70 dark:text-gray-400">
                      Score: {judge.score}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedJudge(index)}
                    className={`p-2 rounded-full ${
                      selectedJudge === index 
                        ? 'bg-primary text-white' 
                        : 'bg-base-300 dark:bg-white/10'
                    }`}
                  >
                    <Volume2 className="h-4 w-4" />
                  </button>
                </div>
                {judge.comment && (
                  <p className="text-sm dark:text-gray-300 italic">
                    "{judge.comment}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        
        {expandedSection === 'feedback' && (
          <div>
            {selectedRoutine.judges && selectedRoutine.judges[selectedJudge] ? (
              <JudgeFeedback 
                judge={selectedRoutine.judges[selectedJudge]} 
                isDarkMode={isDarkMode}
              />
            ) : (
              <div className={`p-4 text-center rounded-lg ${isDarkMode ? "bg-white/5" : "bg-base-200"}`}>
                <p className={isDarkMode ? "text-gray-400" : "text-base-content/70"}>
                  No judge feedback available
                </p>
              </div>
            )}
          </div>
        )}
        
        {expandedSection === 'scores' && (
          <div>
            <ScoreCard 
              routine={selectedRoutine} 
              totalScore={totalScore} 
              award={award}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
      </div>
    </div>
  );

  // Mobile judges quick selector
  const MobileJudgesQuickSelector = () => {
    if (!selectedRoutine || !selectedRoutine.judges || selectedRoutine.judges.length === 0) {
      return null;
    }
    
    return (
      <div className={`fixed bottom-20 left-0 right-0 p-3 transition-opacity duration-300 ${
        isMobileControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="flex justify-center items-center gap-2 max-w-md mx-auto bg-black/60 backdrop-blur-sm p-2 rounded-full">
          <button
            onClick={() => setAllChannelsMode(true)}
            className={`p-2 rounded-full ${
              allChannelsMode ? 'bg-primary text-white' : 'text-white/80'
            }`}
          >
            <Users className="h-4 w-4" />
          </button>
          
          {selectedRoutine.judges.map((judge, index) => (
            <button
              key={index}
              onClick={() => {
                setSelectedJudge(index);
                setAllChannelsMode(false);
              }}
              className={`w-8 h-8 rounded-full overflow-hidden border-2 ${
                selectedJudge === index && !allChannelsMode
                  ? 'border-primary' 
                  : 'border-transparent'
              }`}
              title={judge.name}
            >
              {judge.photoUrl ? (
                <img src={judge.photoUrl} alt={judge.name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-gray-600 text-white text-xs">
                  {judge.name && typeof judge.name === 'string' ? judge.name.charAt(0) : 'J'}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Mobile UI Components */}
      {isMobile && (
        <>
          <MobileRoutineSelector />
          <MobileJudgesQuickSelector />
          <MobileBottomSheet />
        </>
      )}
      
      {/* Mobile Header - Simple version of desktop header */}
      {isMobile ? (
        <div className={`flex justify-between items-center transition-opacity duration-300 ${
          isMobileControlsVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={() => setShowMobileRoutineSelector(true)}
            className="font-medium text-lg dark:text-white flex items-center gap-1"
          >
            {selectedRoutine.title}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-down"><path d="m6 9 6 6 6-6"/></svg>
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 rounded-full bg-base-200 dark:bg-white/10"
            >
              <Share2 className="h-4 w-4 dark:text-white" />
            </button>
            
            <button
              onClick={() => window.open(selectedRoutine.videoUrl, '_blank')}
              className="p-2 rounded-full bg-base-200 dark:bg-white/10"
            >
              <Download className="h-4 w-4 dark:text-white" />
            </button>
          </div>
        </div>
      ) : (
        /* Original Desktop Header */
        <div className="flex justify-between items-center">
          <div className="max-w-[600px]">
            <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-base-content'}`}>
              Critique Library
            </h1>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
              Review and analyze your performance feedback
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowShareModal(true)}
              className={`inline-flex items-center justify-center px-4 py-2 rounded-lg transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-[1.02]' 
                  : 'btn btn-primary gap-2'
              }`}
            >
              <Share2 className="h-5 w-5" />
              <span>Share</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex flex-col ${isMobile ? '' : 'lg:flex-row'} gap-4`}>
        {/* Sidebar on desktop, hidden on mobile (replaced by modal) */}
        {!isMobile && (
          <div className={`w-full ${isMainSidebarCollapsed ? 'lg:w-[300px]' : 'lg:w-[250px]'} shrink-0`}>
            <EventsList 
              events={events}
              selectedEvent={selectedEvent}
              selectedRoutine={selectedRoutine}
              onEventSelect={handleEventSelect}
              onRoutineSelect={handleRoutineSelect}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
        
        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Video player section */}
          <div 
            className="w-full rounded-xl overflow-hidden relative"
            onClick={isMobile ? toggleMobileControls : undefined}
          >
            <VideoPlayer 
              src={selectedRoutine?.videoUrl} 
              selectedJudgeIndex={selectedJudge}
              judgesCount={selectedRoutine?.judges?.length}
              allChannelsMode={allChannelsMode}
              onError={(error) => console.error("Video error:", error)}
            />
          </div>
          
          {/* Title and controls below video - hidden on mobile */}
          {!isMobile && (
            <div className="flex justify-between items-center mt-3">
              <div>
                <h2 className={`text-xl font-medium ${isDarkMode ? 'text-white' : 'text-base-content'}`}>
                  {selectedRoutine.title}
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
                  {selectedRoutine.category}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1 rounded-lg flex items-center gap-2 ${
                  isDarkMode ? 'bg-base-300/50' : 'bg-base-200'
                }`}>
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-base-content'}`}>
                    {totalScore}
                  </span>
                </div>
                
                <div className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-600 font-medium">
                  {award}
                </div>
                
                <button
                  onClick={() => window.open(selectedRoutine.videoUrl, '_blank')}
                  className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-base-300/50 hover:bg-base-300' : 'bg-base-200 hover:bg-base-300'
                  }`}
                  title="Download video"
                >
                  <Download className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* Judges row - hidden on mobile (replaced by bottom sheet) */}
          {!isMobile && (
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => {
                  setAllChannelsMode(!allChannelsMode);
                }}
                className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  allChannelsMode
                    ? isDarkMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-primary text-white'
                    : isDarkMode
                      ? 'bg-base-300/50 text-white/70 hover:bg-base-300' 
                      : 'bg-base-200 hover:bg-base-300'
                }`}
              >
                <Users className="h-5 w-5" />
                <span>All Channels</span>
              </button>
              
              <div className="flex flex-1 gap-2 overflow-x-auto pb-2">
                {selectedRoutine.judges.map((judge, index) => (
                  <JudgeCard
                    key={index}
                    judge={judge}
                    isSelected={selectedJudge === index && !allChannelsMode}
                    onClick={() => {
                      setSelectedJudge(index);
                      setAllChannelsMode(false);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Additional components (Score breakdowns, etc.) - hidden on mobile (shown in bottom sheet) */}
          {!isMobile && (
            <>
              {/* Selected judge feedback */}
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`} />
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-base-content'}`}>
                    Judge Feedback
                  </h3>
                </div>
                
                {selectedRoutine.judges && selectedRoutine.judges[selectedJudge] ? (
                  <JudgeFeedback 
                    judge={selectedRoutine.judges[selectedJudge]} 
                    isDarkMode={isDarkMode}
                  />
                ) : (
                  <div className={`p-4 text-center rounded-lg ${isDarkMode ? "bg-white/5" : "bg-base-200"}`}>
                    <p className={isDarkMode ? "text-gray-400" : "text-base-content/70"}>
                      No judge feedback available
                    </p>
                  </div>
                )}
              </div>
              
              {/* Score breakdown */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Star className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`} />
                    <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-base-content'}`}>
                      Score Breakdown
                    </h3>
                  </div>
                  
                  <button 
                    onClick={() => setShowHistoryModal(true)}
                    className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-primary'}`}
                  >
                    View History
                  </button>
                </div>
                
                <ScoreCard 
                  routine={selectedRoutine} 
                  totalScore={totalScore} 
                  award={award}
                  isDarkMode={isDarkMode}
                />
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Modals */}
      {showShareModal && (
        <ShareModal 
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          routine={selectedRoutine}
          isDarkMode={isDarkMode}
        />
      )}
      
      {showHistoryModal && (
        <PerformanceHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          routine={selectedRoutine}
          selectedEvent={selectedHistoricalEvent}
          onSelectEvent={setSelectedHistoricalEvent}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default CritiquesPage;